import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Upload, Search, Globe, Check } from "lucide-react";
import { BrandLogoImg } from "@/components/BrandLogoPickerDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_META = {
  ai: { label: "AI Tools", emoji: "🤖" },
  dev: { label: "Dev / SaaS", emoji: "🛠" },
  social: { label: "Social", emoji: "📱" },
  custom: { label: "Eigene Marken", emoji: "✨" },
};

/**
 * BrandLogoLibraryTab — read-only grid of the curated brand library.
 * Lets the user inspect every available brand (Claude, ChatGPT, GitHub, etc.)
 * and upload custom logos for the "needs_upload" entries (RAN, compleet, Bundesagentur).
 */
export const BrandLogoLibraryTab = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [uploadingSlug, setUploadingSlug] = useState(null);
  const fileInputRefs = useRef({});
  // bump key to force <img> reload after a custom upload (cache busts the GET endpoint)
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/brand-logos`)
      .then((res) => {
        if (cancelled) return;
        setBrands(res.data.brands || []);
      })
      .catch(() => {
        if (!cancelled) toast.error("Marken-Bibliothek konnte nicht geladen werden");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(brands.map((b) => b.category)));
    return cats.sort();
  }, [brands]);

  const filtered = useMemo(() => {
    let res = brands;
    if (activeCat !== "all") res = res.filter((b) => b.category === activeCat);
    if (search.trim()) {
      const q = search.toLowerCase();
      res = res.filter(
        (b) =>
          b.name.toLowerCase().includes(q) ||
          b.aliases?.some((a) => a.toLowerCase().includes(q))
      );
    }
    return res;
  }, [brands, activeCat, search]);

  const handleUploadClick = (slug) => {
    fileInputRefs.current[slug]?.click();
  };

  const handleUploadFile = async (slug, file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 4 MB)");
      return;
    }
    setUploadingSlug(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/brand-logos/${slug}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Logo für „${slug}" hochgeladen (${res.data.size_kb} KB)`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingSlug(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <Card className="p-4 bg-primary/5 border-primary/20">
        <div className="flex items-start gap-3">
          <Globe className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-semibold">Marken-Bibliothek</p>
            <p className="text-muted-foreground mt-0.5">
              {brands.length} kuratierte Logos für AI-Tools, Dev-/SaaS-Marken und Social-Plattformen.
              Beim Thumbnail/Meta-Ad Wizard kannst du sie auswählen — die KI bekommt sie als Referenz
              und rendert sie pixel-genau ins Bild. Eigene Marken (RAN, compleet, Bundesagentur) kannst du hier hochladen.
            </p>
          </div>
        </div>
      </Card>

      {/* Search + category pills */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Marke suchen (Claude, GitHub, Stripe...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="brand-library-search"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCat("all")}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            activeCat === "all"
              ? "bg-primary text-primary-foreground"
              : "bg-muted hover:bg-muted/80 text-muted-foreground"
          }`}
          data-testid="brand-cat-all"
        >
          Alle ({brands.length})
        </button>
        {categories.map((cat) => {
          const meta = CATEGORY_META[cat] || { label: cat, emoji: "🔖" };
          const count = brands.filter((b) => b.category === cat).length;
          return (
            <button
              key={cat}
              onClick={() => setActiveCat(cat)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeCat === cat
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted hover:bg-muted/80 text-muted-foreground"
              }`}
              data-testid={`brand-cat-${cat}`}
            >
              {meta.emoji} {meta.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Lade Marken...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Keine Marken gefunden für „{search}"
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filtered.map((brand) => (
            <Card
              key={brand.slug}
              className="overflow-hidden group hover:shadow-md transition-shadow"
              data-testid={`brand-library-card-${brand.slug}`}
            >
              <div
                className="aspect-square bg-muted/30 flex items-center justify-center p-4 relative"
                key={`${brand.slug}-${reloadKey}`}
              >
                <BrandLogoImg slug={brand.slug} name={brand.name} size="w-full h-full" />
                {brand.needs_upload && (
                  <Badge
                    variant="outline"
                    className="absolute top-2 left-2 text-[10px] bg-background/80 backdrop-blur"
                  >
                    Eigen
                  </Badge>
                )}
              </div>
              <div className="p-3 space-y-1">
                <p className="font-medium text-sm truncate" title={brand.name}>
                  {brand.name}
                </p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {CATEGORY_META[brand.category]?.emoji} {brand.category}
                </p>
                {brand.needs_upload || !brand.available ? (
                  <>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml"
                      className="hidden"
                      ref={(el) => (fileInputRefs.current[brand.slug] = el)}
                      onChange={(e) => handleUploadFile(brand.slug, e.target.files?.[0])}
                    />
                    <Button
                      variant={brand.has_custom_upload ? "outline" : "default"}
                      size="sm"
                      className="w-full h-8 mt-1.5"
                      onClick={() => handleUploadClick(brand.slug)}
                      disabled={uploadingSlug === brand.slug}
                      data-testid={`brand-upload-${brand.slug}`}
                    >
                      {uploadingSlug === brand.slug ? (
                        "Lade hoch..."
                      ) : brand.has_custom_upload ? (
                        <>
                          <Check className="w-3 h-3 mr-1.5 text-green-500" />
                          Logo ersetzen
                        </>
                      ) : (
                        <>
                          <Upload className="w-3 h-3 mr-1.5" />
                          Logo hochladen
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <Check className="w-3 h-3 text-green-500" /> verfügbar
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BrandLogoLibraryTab;
